import { Tooltip, icons } from '@lifebit-ai/lds'
import React from 'react'

import { PencilIcon, TagIcon, TagLabel, TagWrapper, UnassignedTag } from 'modules/resultsV2/Results.styles'
import { formatDate } from 'utils/date'

import * as styles from './RequestMetaTags.styles'

export const REQUESTED_BY_MEANING = 'Requested by'
export const REQUESTED_ON_MEANING = 'Requested on'
export const WORKSPACE_MEANING = 'Workspace name'
export const REQUEST_TYPE_MEANING = 'Request type'
export const ASSIGNEE_MEANING = 'Assignee'

export interface RequestMetaTagProps {
  /** What this fact is, e.g. "Requested by" — shown on hover and read out to assistive tech. */
  meaning: string
  icon: React.ReactNode
  children: React.ReactNode
}

/**
 * One fact about a request in the page header. Each carries its own icon and names itself on
 * hover, so the header reads as a row of separate values rather than one long sentence.
 */
export const RequestMetaTag = ({ meaning, icon, children }: RequestMetaTagProps): JSX.Element => (
  <Tooltip title={meaning}>
    <TagWrapper aria-label={meaning}>
      <TagIcon>{icon}</TagIcon>
      <TagLabel>{children}</TagLabel>
    </TagWrapper>
  </Tooltip>
)

export const RequestedByTag = ({ email }: { email: string }): JSX.Element => (
  <RequestMetaTag meaning={REQUESTED_BY_MEANING} icon={<icons.UserSmall />}>
    {email}
  </RequestMetaTag>
)

export const RequestedOnTag = ({ at }: { at: string }): JSX.Element => (
  <RequestMetaTag meaning={REQUESTED_ON_MEANING} icon={<icons.CalendarSmall />}>
    {formatDate(at)}
  </RequestMetaTag>
)

export const WorkspaceTag = ({ name }: { name: string }): JSX.Element => (
  <RequestMetaTag meaning={WORKSPACE_MEANING} icon={<icons.LifebitSmall />}>
    {name}
  </RequestMetaTag>
)

export const RequestTypeTag = ({ icon, label }: { icon: React.ReactNode; label: string }): JSX.Element => (
  <RequestMetaTag meaning={REQUEST_TYPE_MEANING} icon={icon}>
    {label}
  </RequestMetaTag>
)

export const AssigneeTag = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <RequestMetaTag meaning={ASSIGNEE_MEANING} icon={<icons.AddPersonSmall />}>
    {children}
  </RequestMetaTag>
)

export const UNASSIGNED_LABEL = 'Unassigned'
export const CHANGE_ASSIGNEE_LABEL = 'Change assignee'

export interface EditableAssigneeTagProps {
  email?: string
  /** Whether the request is still at a point where the assignee can be changed. */
  isEditable: boolean
  onEdit: () => void
}

/**
 * The assignee, always shown — "Unassigned" is a state worth seeing, not an absence to hide — and
 * editable in place wherever the request still accepts a change.
 */
const EditAssignee = ({ onEdit }: { onEdit: () => void }) => (
  <styles.EditAssigneeButton aria-label={CHANGE_ASSIGNEE_LABEL} onClick={onEdit} type="button">
    <PencilIcon />
  </styles.EditAssigneeButton>
)

export const EditableAssigneeTag = ({ email, isEditable, onEdit }: EditableAssigneeTagProps): JSX.Element => (
  <AssigneeTag>
    {email ? (
      <>
        <span>{email}</span>
        {isEditable && <EditAssignee onEdit={onEdit} />}
      </>
    ) : (
      <UnassignedTag>
        <span>{UNASSIGNED_LABEL}</span>
        {isEditable && <EditAssignee onEdit={onEdit} />}
      </UnassignedTag>
    )}
  </AssigneeTag>
)
